import React from 'react';
import { Panel, Tab, Row, Col, Nav, NavItem } from 'react-bootstrap';
import Store from 'stores/TariffsStore';
import { extend, each } from 'lodash';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import PropertiesEditor from './TariffPropsEditor';
import PricesTable from './TariffPricesTable';
import OtherPropertiesEditor from './TariffOtherPropsEditor';
import TariffShippersTable from './TariffShippersTable';

const kDefaultSlug = 'truckload_tariff';
const kDefaultType = 'tabular';
const kBoxToPalletConversionRate = 67;
const kDefaultMarkets = ['LAX'];

const isFloatZero = (a) => {
  return a?.toFixed(2) === '0.00';
};

class TariffEditor extends React.Component {
  tariff = null;
  propertiesValid = true;
  priceMatrixValid = true;

  componentDidMount() {
    Store.addChangeListener(this.onChange);
  }

  componentWillUnmount() {
    Store.removeChangeListener(this.onChange);
  }

  onChange = () => {
    if (Store.getStatus() === 'success') {
      const propsTariff = this.props.tariff;
      const storeTariff = Store.getTariff();
      if (storeTariff._id) {
        this.tariff = extend({}, storeTariff);
      } else {
        this.tariff = extend({}, propsTariff);
      }
      this.propertiesValid = true;
      this.priceMatrixValid = true;
      this.notifyUpdate(false);
    } else {
      this.notifyUpdate(true);
    }
  };

  handlePropertiesChange = () => {
    return (key, value) => {
      this.tariff[key] = value;
      this.propertiesValid = this.tariff.name && this.tariff.name.length > 0;
      this.notifyUpdate(true);
    };
  };

  handlePricesDataChange = () => {
    return (pricesData) => {
      const pricesValid = this.validatePrices(pricesData);
      const zonesValid = this.validateZones(pricesData);
      Object.keys(pricesData).forEach((property) => {
        if (property) {
          this.tariff[property] = pricesData[property];
        }
      });
      this.priceMatrixValid = zonesValid && pricesValid;
      this.notifyUpdate(true);
    };
  };

  validateZones = (pricesData) => {
    let valid = true;
    if (pricesData.zones) {
      for (let index = 0; index < pricesData.zones.length; ++index) {
        const zone = pricesData.zones[index];
        if (index) {
          valid = zone[0] > 0 && zone[1] >= zone[0];
        } else {
          valid = zone[1] > 0;
        }
        if (!valid) {
          break;
        }
      }
    }
    return valid;
  };

  validatePrices = (pricesData) => {
    let valid = true;
    if (pricesData.zone_to_load) {
      for (let index = 0; index < pricesData.zone_to_load.length; ++index) {
        const row = pricesData.zone_to_load[index];
        for (let rowIndex = 0; rowIndex < row.length; ++rowIndex) {
          const price = row[rowIndex];
          if (isFloatZero(price)) {
            valid = false;
            break;
          }
        }
      }
    }
    return valid;
  };

  notifyUpdate = (edited) => {
    if (this.props.onChange) {
      const validated = this.propertiesValid && this.priceMatrixValid;
      this.props.onChange(this.tariff, edited, validated);
    }
  };

  newTariff = () => {
    return {
      name: '',
      slug: kDefaultSlug,
      type: kDefaultType,
      markets: kDefaultMarkets,
      zones: [],
      weight_ranges: [],
      load_ranges: [],
      zone_to_weight: [],
      zone_to_load: [],
      minimum_distance: 0,
      minimum_weight: 0,
      minimum_cost: 0,
      maximum_cost: 0,
      cost_per_extra_mile: 0,
      cost_per_extra_lb: 0,
      is_default: false,
      box_to_pallet_conversion_rate: kBoxToPalletConversionRate,
    };
  };

  render() {
    const { t } = this.props;
    if (!this.tariff) {
      this.tariff = this.props.tariff || this.newTariff();
    }
    const disableShippers = !(this.tariff && this.tariff._id);
    return (
      <div>
        <Tab.Container id="left-tabs-example" defaultActiveKey="1">
          <Row className="clearfix">
            <Col sm={2}>
              <Nav bsStyle="pills" stacked>
                <NavItem eventKey="1">{t('PROPERTIES')}</NavItem>
                <NavItem eventKey="2" disabled={disableShippers}>
                  {titleize(t('SHIPPER', { count: 2 }))}
                </NavItem>
                <NavItem eventKey="3">
                  {titleize(t('PRICE', { count: 2 }))}
                </NavItem>
                <NavItem eventKey="4">{t('OTHER')}</NavItem>
              </Nav>
            </Col>
            <Col sm={10}>
              <Tab.Content animation>
                <Tab.Pane eventKey="1">
                  <Panel>
                    <PropertiesEditor
                      tariff={this.tariff}
                      tariffsList={this.props.tariffsList}
                      onChange={this.handlePropertiesChange()}
                      fill
                    />
                  </Panel>
                </Tab.Pane>
                <Tab.Pane eventKey="2">
                  <Panel className="table-responsive">
                    <TariffShippersTable tariff={this.tariff} />
                  </Panel>
                </Tab.Pane>
                <Tab.Pane eventKey="3">
                  <Panel className="table-responsive">
                    <PricesTable
                      tariff={this.tariff}
                      onChange={this.handlePricesDataChange()}
                    />
                  </Panel>
                </Tab.Pane>
                <Tab.Pane eventKey="4">
                  <Panel>
                    <OtherPropertiesEditor
                      tariff={this.tariff}
                      onChange={this.handlePropertiesChange()}
                    />
                  </Panel>
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </div>
    );
  }
}

export default withTranslation()(TariffEditor);
